<template>
  <div data-app>
    <div class="filter-box md-layout-item md-size-100 md-small-size-100 mt-5">
      <div
        class="application-popup-overlay"
        v-if="showApplications"
        v-on:click="showApplications = false"
      />

      <div class="application-popup" v-if="showApplications">
        <application-list
          v-if="totalApplications && totalApplications.length > 0"
          :items="totalApplications"
          :backend-pagination="true"
          :external-data-refresh="true"
          :external-loading-pagination="paginationLoading"
          @update="openApplicationEditor"
          @view="openApplicationSummary"
          @update-status="openUpdateStatusDialog"
          @manage-owners="openManageOwnersDialog"
          @attachments="openApplicationDocuments"
          @comment="openCommentDialog"
          @clone="onCloneApplication"
          @refresh-data="refreshData"
        />
        <md-subheader v-else>No applications found</md-subheader>
      </div>
    </div>
    <div class="md-layout input-layout">
      <!-- From date field-->
      <div class="md-layout-item md-small-size-50 md-size-15">
        <md-datepicker
          md-immediately
          class="reporting-date-picker"
          v-model="filters.date_filter.start_date"
        >
          <label>From date</label>
        </md-datepicker>
      </div>

      <!-- To date field-->
      <div class="md-layout-item md-small-size-50 md-size-15">
        <md-datepicker
          md-immediately
          v-model="filters.date_filter.end_date"
        >
          <label>To date</label>
        </md-datepicker>
      </div>
      <!-- asset type field-->
      <validated-autocomplete-input
        class="md-layout-item md-small-size-100 md-size-10"
        label="Asset Types"
        v-model="filters.dropdown.asset_type"
        :options="options.loan_asset_type"
        v-if="options.loan_asset_type"
        force
        default-option
        multiple
        :focus="true"
        :disabled="currentTabId === 'tab-leads'"
      >
      </validated-autocomplete-input>
      <!-- current pods field-->
      <validated-autocomplete-input
        class="md-layout-item md-small-size-100 md-size-15"
        label="Pods"
        v-model="filters.dropdown.pod"
        :options="options.broker_pods"
        v-if="options.broker_pods"
        force
        :focus="true"
        multiple
      >
      </validated-autocomplete-input>
      <!-- broker field -->
      <validated-autocomplete-input
        v-if="filters.dropdown.pod"
        class="md-layout-item md-small-size-100 md-size-10"
        label="Broker"
        v-model="filters.dropdown.broker"
        :options="podBrokerList"
        :isInactiveUser="userTagsStatuses"
        force
        default-option
        multiple
        :focus="true"
      >
      </validated-autocomplete-input>
      <!--role type field-->
      <validated-autocomplete-input
        class="md-layout-item md-small-size-100 md-size-10"
        label="Roles"
        v-model="filters.dropdown.roles"
        :options="options.roles"
        v-if="options.roles"
        force
        default-option
        multiple
        :focus="true"
        :disabled="currentTabId === 'tab-leads'"
      >
      </validated-autocomplete-input>
      <!-- lender field-->
      <validated-autocomplete-input
        class="md-layout-item md-small-size-100 md-size-15"
        label="Lender"
        v-model="filters.dropdown.lender"
        :options="this.lenderList"
        v-if="this.lenderList"
        force
        default-option
        multiple
        :focus="true"
        :disabled="currentTabId === 'tab-leads'"
      >
      </validated-autocomplete-input>
      <!-- introducer field-->
      <span class="md-layout-item md-small-size-100 md-size-10">
        <md-autocomplete
          :md-options="structuredIntroducerList"
          v-model="filters.dropdown.introducer"
          :disabled="currentTabId === 'tab-leads'"
        >
          <label>Referrer</label>
          <template slot="md-autocomplete-item" slot-scope="{ item }">
            <div v-if="item.isParent" class="parent-referrer">{{ item.name }}</div>
            <div v-else class="child-referrer">{{ '-- ' + item.name }}</div>
          </template>
        </md-autocomplete>
      </span>
      <!-- child pods field -->
      <div class="md-layout-item md-size-40"></div>
      <div class="md-layout-item md-small-size-100 md-size-15">
        <v-autocomplete
          v-if="hasChildPods"
          v-model="filters.dropdown.child_pods"
          :background-color="setBgColorDropdown()"
          :items="getChildPods"
          dense
          chips
          filled
          small-chips
          @mouseover.native="toggleDropdownPreview('pods', true)"
          @mouseout.native="toggleDropdownPreview('pods', false)"
          multiple
          item-value="id"
          item-text="description"
          label="Child Pods"
          ref="pods"
          @blur="blurDropdown('pods')"
        >
          <template #selection="{ item }">
            <v-chip
              ref="pods_chips"
              :active="showDropdown.pods"
            >{{ item.name }}</v-chip>
          </template>
          <template v-slot:prepend-item>
              <v-list-item
                ripple
                @mousedown.prevent
                @click="toggle()"
              >
                    <v-list-item-action>
                      <v-icon>
                        {{ podIcon }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title>
                        Select All
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
          </template>
        </v-autocomplete>
      </div>
    </div>
    <div v-if="showAssetFilters" class="md-layout input-layout">
      <!-- Asset filters -->
      <div v-if="showAssetFilters" class="md-layout input-layout" style="padding-bottom: 20px;">
        <!-- Make -->
        <validated-autocomplete-input
          class="md-layout-item md-small-size-100 md-size-20"
          label="Make"
          v-model="filters.dropdown.make"
          :options="all_makers"
        >
        </validated-autocomplete-input>
        <!-- Model -->
        <validated-autocomplete-input
          class="md-layout-item md-small-size-30 md-size-20"
          label="Model"
          v-model="filters.dropdown.model"
          :options="models_make"
        >
        </validated-autocomplete-input>
        <!-- Model Year -->
        <validated-autocomplete-input
          class="md-layout-item md-small-size-100 md-size-20"
          label="Model Year"
          v-model="filters.dropdown.model_year"
          :options="options.model_years"
          force
        >
        </validated-autocomplete-input>
      </div>
    </div>
    <br>
      <md-card-actions>
        <loading-icon-text-button
          class="md-primary md-sm"
          icon="assignment"
          :loading="false"
          v-if="(this.applicationIds.length > 0) && this.allowSeeApplications"
          @click="getApplicationList(applicationIds)"
        >
          See Applications
        </loading-icon-text-button>
        <loading-icon-text-button
          class="md-primary md-sm"
          icon="file_download"
          :loading="false"
          @click="exportApplications"
          v-if="this.allowExport"
        >
          Export
        </loading-icon-text-button>
        <loading-icon-text-button
          class="md-primary md-sm"
          icon="restart_alt"
          :loading="false"
          @click="resetFilters"
        >
          Reset
        </loading-icon-text-button>
        <md-button
          class="md-primary md-sm"
          @click="applyFilter()"
        >
          Apply
        </md-button>
      </md-card-actions>
    <md-tabs class="md-secondary section-tabs" md-alignment="centered" @md-changed="tabChanged" :md-active-tab.sync="currentTabId">
      <md-tab id="tab-sales" md-label="Sales">
        <sales-reporting :salesTabStats="salesTabStats" :salesTabGraph="salesTabGraph" :roundedNaf="roundedNaf" :roundedTotalIncome="roundedTotalIncome" :filters="filters"/>
      </md-tab>
      <md-tab id="tab-pipeline" md-label="Pipeline">
        <pipeline-reporting :pipelineTabStats="pipelineTabStats" :pipelineTabGraph="pipelineTabGraph" :filters="filters" @show="getApplicationList">
          <template v-slot:cards-slot>
            <settlement-reporting :settlementTabStats="settlementTabStats" :filters="filters" @show="getApplicationList"/>
          </template>
        </pipeline-reporting>
      </md-tab>
      <md-tab id="tab-compliance" md-label="Compliance">
        <compliance-reporting :complianceTabStats="complianceTabStats" :filters="filters" @show="getApplicationList"/>
      </md-tab>
      <md-tab id="tab-services" v-if="showServiceTab" md-label="Services">
        <services-reporting :servicesTabStats="servicesTabStats" :filters="filters" @show="getApplicationList"/>
      </md-tab>
      <md-tab id="tab-billings" v-if="showBillings" md-label="Billings">
        <billings-reporting :billingsTabStats="billingsTabStats" :billingsTabGraph="billingsTabGraph" :filters="filters"/>
      </md-tab>
      <md-tab id="tab-leads" v-if="showLeads" md-label="Leads">
        <leads-reporting :leadsTabStats="leadsTabStats" :filters="filters"/>
      </md-tab>
      <md-tab id="tab-broker-auction" v-if="showBrokerAuctionTab" md-label="BrokerAuction">
        <broker-auction-reporting />
      </md-tab>
      <md-tab id="tab-broker-sale" v-if="showBrokerSaleTab" md-label="BrokerSale">
        <broker-sale-reporting />
      </md-tab>
      <md-tab id="tab-meta-billing" v-if="showBillingMetaReporting" md-label="Billing Meta Report"  >
        <billing-meta-reporting />
      </md-tab>
    </md-tabs>

    <group-card v-if="this.firstLoad && (this.currentTabId != 'tab-broker-auction' || this.currentTabId != 'tab-broker-sale')">
      <md-empty-state
        md-description="Apply filter to fetch the reporting data."
      />
    </group-card>
  </div>
</template>

<script>
import BillingMetaReporting from "@/pages/BillingMetaReporting";
import SalesReporting from "@/pages/SalesReporting";
import BillingsReporting from "@/pages/BillingsReporting";
import PipelineReporting from "@/pages/PipelineReporting";
import SettlementReporting from  "@/pages/SettlementReporting" ;
import ComplianceReporting from "@/pages/ComplianceReporting";
import ServicesReporting from "@/pages/ServicesReporting";
import LeadsReporting from "@/pages/LeadsReporting";
import SubmissionReporting from "@/pages/SubmissionReporting";
import BrokerAuctionReporting from "@/pages/BrokerAuctionReporting.vue";
import BrokerSaleReporting from "@/pages/BrokerSaleReporting.vue";
import StatCardWithIcon from "./ReportingDashboard/components/StatCardWithIcon";
import BarChart from "./ReportingDashboard/charts/BarChart";
import ValidatedInput from "../components/ValidatedInput/ValidatedInput";
import ValidatedSelectInput from "../components/ValidatedInput/ValidatedSelectInput";
import GroupCard from "../components/Cards/GroupCard";
import LoadingIconTextButton from "../components/LoadingIconTextButton";
import {mapGetters} from "vuex";
import ValidatedAutocompleteInput from "../components/ValidatedInput/ValidatedAutocompleteInput";
import WaitingDialog from "../components/Dialogs/WaitingDialog";
import {newReportingFilters} from "../schema";
import PieChart from "./ReportingDashboard/charts/PieChart";
import DoughChart from "./ReportingDashboard/charts/DoughChart";
import NotificationCard from "../components/Cards/NotificationCard";
import LineChart from "./ReportingDashboard/charts/LineChart";
import AlertDialog from "../components/Dialogs/AlertDialog";
import ApplicationList from "@/components/ApplicationList";
import ConfirmDialog from "@/components/Dialogs/ConfirmDialog";
import OptionsDialog from "@/components/Dialogs/OptionsDialog";
import moment from "moment";

export default {
  components: {
    BillingMetaReporting,
    SalesReporting,
    BillingsReporting,
    PipelineReporting,
    SettlementReporting,
    ComplianceReporting,
    LeadsReporting,
    ServicesReporting,
    SubmissionReporting,
    BrokerAuctionReporting,
    BrokerSaleReporting,
    NotificationCard,
    DoughChart,
    ValidatedInput,
    StatCardWithIcon,
    BarChart,
    GroupCard,
    ValidatedSelectInput,
    LoadingIconTextButton,
    ValidatedAutocompleteInput,
    PieChart,
    LineChart,
    ApplicationList,
    ConfirmDialog,
    WaitingDialog,
    AlertDialog,
    GroupCard
  },
  data() {
    return {
      temporaryPodSelection: [],
      temporaryChildPodSelection: [],
      bgColorClass: undefined,
      showDropdown: {
        pods: false,
      },
      searchPods: [],
      showAssetFilters: false,
      brokerList: [],
      podBrokerList: [],
      introducerList: [],
      lenderList: [],
      graphOption: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          datalabels: {
            color: 'black',
            backgroundColor: 'white',
            textAlign: 'center',
            borderRadius: 5,
          }
        },
      },
      filters: new newReportingFilters(),
      showApplications: false,
      salesTabStats: [],
      salesTabGraph: [],
      billingsTabStats: [],
      billingsTabGraph: [],
      pipelineTabStats: [],
      pipelineTabGraph: [],
      settlementTabStats: [],
      complianceTabStats: [],
      leadsTabStats: [],
      servicesTabStats: [],
      currentTabId: "tab-sales",
      allowExport: false,
      allowSeeApplications: false,
      updateRequireForSalesTab: true,
      updateRequireForBillingsTab: true,
      updateRequireForPipelineTab: true,
      updateRequireForSubmissionTab: true,
      updateRequireForSettlementTab: true,
      updateRequireForComplianceTab: true,
      updateRequireForServiceTab: true,
      updateRequireForLeadsTab: true,
      roundedNaf: [],
      roundedTotalIncome: [],
      totalApplications: [],
      applicationIds: [],
      showServiceTab: false,
      showSettlementTab: false,
      showBillings: false,
      showLeads: false,
      showBrokerAuctionTab: false,
      showBrokerSaleTab: false,
      showBillingMetaReporting: false,
      firstLoad: true,
      initialTabSetup: true,
      tabReference: {
        'sales': 'tab-sales',
        'pipeline': 'tab-pipeline',
        'compliance': 'tab-compliance',
        'leads': 'tab-leads',
        'services': 'tab-services',
        'billings': 'tab-billings',
        'broker-auction': 'tab-broker-auction',
        'broker-sale': 'tab-broker-sale',
        'billing-meta-report': 'tab-meta-billing'
      },
      paginationLoading: false
    };
  },
  methods: {
    toggle () {
        this.$nextTick(() => {
            if (this.allChildPods) {
                this.filters.dropdown.child_pods = []
            } else {
                this.filters.dropdown.child_pods = [];
                for (let i in this.getChildPods) {
                    this.filters.dropdown.child_pods.push(this.getChildPods[i].id)
                }
            }
        });
    },
    blurDropdown(event) {
      this.$refs[event].isMenuActive = false;
      this.isActiveChips = false;
      this.setBgColorDropdown();
    },
    setBgColorDropdown() {
        this.filters.dropdown.child_pods.length > 0 ? this.bgColorClass = 'lightgreen' : this.bgColorClass = undefined;
    },
    toggleDropdownPreview(type, isActive) {
      if (isActive) {
        this.showDropdown[type] = true;
      } else {
        this.showDropdown[type] = false;
      }
    },
    async fetch_all_make() {
      await this.$store.dispatch("load_all_make");
    },
    async fetch_model_by_make() {
      if (this.filters.dropdown.make) {
        let payload = {
          make: this.filters.dropdown.make,
        };
        await this.$store.dispatch("load_model_make", payload);
      }
    },
    tabChanged(id) {

      // Ensure the id is valid before proceeding
      if (!id || this.initialTabSetup) {
        console.warn("Ignoring tabChanged due to initial setup or invalid id:", id);
        return;
      }

      this.currentTabId = id;

      // Update the route with an absolute path to ensure it's correctly set
      this.$router.push({
        path: `/application/${this.$route.params.id}/reporting-${id.replace('tab-', '')}`
      }).catch(err => {
        // Prevent the navigation duplication error when navigating to the same route
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });

      // Skip data fetch on first load
      if (this.firstLoad) {
        this.firstLoad = false; // Set firstLoad to false after the initial load
        this.getReportingData(this.currentTabId, true);
        return;
      }

      // Fetch data for the new tab
      this.getReportingData(id, true);
    },
    updateTabFromRoute(tabParam) {
      const tabId = this.tabReference[tabParam || this.currentTabId] ;

      if (tabId) {
        this.currentTabId = tabId;
        this.initialTabSetup = false; // Disable initial tab setup after the first route setup

        // Force the DOM to update, then manually trigger the tab change
        this.$nextTick(() => {
          this.tabChanged(tabId);
        });

      } else {
        this.currentTabId = 'null'; // Handle invalid tab parameter
      }
    },
    isBrokerActive(name) {
        if (!name || !this.brokerList.statuses) {
            return false;
        }

        return this.brokerList.statuses[name] !== 'Active';
    },
    async getReportingData(id, force = false) {
      try {
        this.showModal('Please wait while the report is being generated.', 'Loading....');
        switch (id) {
          case "tab-sales":
            //fetch reporting for sales
            if (this.updateRequireForSalesTab == true || force) {
              const salesData = await axios.post("/api/v1/reporting/getReportDataForSalesTab", {filter: this.filters});
              if (this.filters.export == false) {
                this.salesTabStats = salesData.data.processedFigures;
                this.salesTabGraph = salesData.data.graph;
                this.applicationIds = salesData.data.appIds;
                this.roundedNaf = this.salesTabGraph.lenderBreakDownNAF.datasets[0].data.map(naf => {
                    return Math.round(naf);
                })
                this.roundedTotalIncome = this.salesTabGraph.lenderBreakDown.datasets[0].data.map(income => {
                    return Math.round(income);
                })
                this.updateRequireForSalesTab = false;
              }
            }
            this.allowExport = true;
            this.allowSeeApplications = true;
            break;
          case "tab-billings":
            //fetch reporting for billings
            if (this.updateRequireForBillingsTab == true || force) {
              const billingsData = await axios.post("/api/v1/reporting/getReportDataForBillingsTab", {filter: this.filters});
              if (this.filters.export == false) {
                this.billingsTabStats = billingsData.data.processedFigures;
                this.billingsTabGraph = billingsData.data.graph;
                this.updateRequireForBillingsTab = false;
              }
            }
            this.allowExport = true;
            this.allowSeeApplications = false;
            break;
          case "tab-pipeline":
            //fetch reporting for pipeline
            if (this.updateRequireForPipelineTab == true || force) {
              const pipelineData = await axios.post("/api/v1/reporting/getReportDataForPipelineTab", {filter: this.filters});
              this.pipelineTabStats = pipelineData.data.processedFigures;
              this.pipelineTabGraph = pipelineData.data.graph;
              this.updateRequireForPipelineTab = false;
            }
            if (this.updateRequireForSettlementTab == true || force) {
              const settlementData = await axios.post("/api/v1/reporting/getReportDataForSettlementTab", {filter: this.filters});
              this.settlementTabStats = settlementData.data.processedFigures;
              this.updateRequireForSettlementTab = false;
            }
            this.allowExport = false;
            this.allowSeeApplications = false;
            break;
          case "tab-compliance":
            //fetch reporting for compliance
            if (this.updateRequireForComplianceTab == true || force) {
              const complianceData = await axios.post("/api/v1/reporting/getReportDataForComplianceTab", {filter: this.filters});
              this.complianceTabStats = complianceData.data.processedFigures;
              this.updateRequireForComplianceTab = false;
            }
            this.allowExport = false;
            this.allowSeeApplications = false;
            break;
          case "tab-services":
            //fetch reporting for services
            if (this.updateRequireForServiceTab == true || force) {
              const servicesData = await axios.post("/api/v1/reporting/getReportDataForServicesTab", {filter: this.filters});
              this.servicesTabStats = servicesData.data.processedFigures;
              this.updateRequireForServiceTab = false;
            }
            this.allowExport = false;
            this.allowSeeApplications = false;
            break;
          case "tab-leads":
            //fetch reporting for leads
            if (this.updateRequireForLeadsTab == true || force) {
              const leadsData = await axios.post("/api/v1/reporting/getReportDataForLeadsTab", {filter: this.filters, isPodManager: this.isPodManager});
              this.leadsTabStats = leadsData.data.data;
              this.updateRequireForLeadsTab = false;
            }
            this.allowExport = false;
            this.allowSeeApplications = false;
            break;
          default :
            break;
        }
      } catch (err) {
        console.log(err)
      } finally {
        this.closeModal();
      }
    },
    showModal(message, title) {
      this.$modal.show(WaitingDialog, {
        title: title,
        message: message,
      });
    },
    closeModal() {
      if (this.$modal.submit) {
        this.$modal.submit();
      }
    },
    async getPreGraphData(force = false) {
      try {
        this.showModal('Please wait while the filtering is being generated.', 'Loading....');
        let response = await this.$store.dispatch("ensure_pre_graph_data", force);
        this.brokerList = response.brokerList;
        this.introducerList = response.introducerList;
        this.lenderList = response.lenderList;
        this.getPodBasedBrokers(this.filters.dropdown.pod); // initialization by default 'All' pod dependencies
        this.addExternalBroker();
      } catch (e) {
        console.error(e);
      } finally {
        this.addExclFloorPlan();
        this.closeModal();
      }
    },
    async updateBrokersAndReferrersDueToChildPodUpdate()
    {
        let pod = this.filters.dropdown.pod;
        await this.getPodBasedBrokers(pod);
    },
    async getPodBasedBrokers(pod) {
      if (pod != 'External Brokers' && pod != 'All') {
        this.podBrokerList = this.brokerList.brokers[pod];
        if (this.filters.dropdown.child_pods.length) {
            let allPods = this.getPods;
            let childPodIds = this.filters.dropdown.child_pods;
            let childPods = allPods
                .filter(item => childPodIds.includes(item.id)) // Find objects matching the given IDs
                .map(item => item.name); // Extract the "name" property

            if (childPods && childPods.length) {
                let newList = new Object();
                childPods.push(pod); // add parent pod
                Object.keys(this.brokerList.brokers).reduce((obj, key) => {
                    if (childPods.includes(key)) {
                        Object.keys(this.brokerList.brokers[key]).forEach((email) => {
                            newList[email] = this.brokerList.brokers[key][email];
                        });
                    }
                    return obj;
                }, {});
                this.podBrokerList = newList;
            }
        }
      } else {
        let newList = new Object();
        if (pod == 'All') {
          //if key is all show all the brokers
          Object.keys(this.brokerList.brokers).reduce((obj, key) => {
            Object.keys(this.brokerList.brokers[key]).forEach((email) => {
              newList[email] = this.brokerList.brokers[key][email];
            });
            return obj;
          }, {});
        } else {
          //if key is internal broker exclude it
          Object.keys(this.brokerList.brokers).reduce((obj, key) => {
            if (key != 'Internal Broker') {
              Object.keys(this.brokerList.brokers[key]).forEach((email) => {
                newList[email] = this.brokerList.brokers[key][email];
              });
            }
            return obj;
          }, {});
        }
        this.podBrokerList = newList;
      }
    },
    addExclFloorPlan() {
      //add "excl floorplan lenders" to the front of options.lenders
      this.lenderList.unshift("Exclude Floorplan Lenders");
      this.lenderList.unshift("All");
    },
    addExternalBroker(){
      //add external brokers pod to the front of options.brokers
      this.options.broker_pods.push("External Brokers");

      //add All Optiosn to roles
      this.options.roles.unshift("All");
      //add all to the front
      this.options.broker_pods.unshift("All");
    },
    resetFilters() {
      this.filters = newReportingFilters();
      this.setRequireToFetchNewDataForAllTab();
      this.getReportingData(this.currentTabId);
    },
    applyFilter() {
      this.firstLoad = false;
      this.setRequireToFetchNewDataForAllTab();
      this.getReportingData(this.currentTabId);
    },
    setRequireToFetchNewDataForAllTab() {
      this.updateRequireForSalesTab = true;
      this.updateRequireForBillingsTab = true;
      this.updateRequireForPipelineTab = true;
      this.updateRequireForSubmissionTab = true;
      this.updateRequireForSettlementTab = true;
      this.updateRequireForComplianceTab = true;
      this.updateRequireForServiceTab = true;
      this.updateRequireForLeadsTab = true;
    },
    async exportApplications(){
      this.filters.force = true;
      this.filters.export = true;
      await this.getReportingData(this.currentTabId, true);
      this.filters.export = false;
      this.filters.force = false;
      let notificationMessage = "Your export has been queued and will be processed in order received. Note during peak periods this may mean wait times from 15mins to over an hour.  Repeated requests will extend wait times.";
      if (
        (this.currentTabId == 'tab-billings' && this.billingsTabStats && this.billingsTabStats[0] && this.billingsTabStats[0].billing_data_count > 100) ||
        (this.currentTabId == 'tab-sales' && this.applicationIds && this.applicationIds.length > 100)
      ) {
        notificationMessage = "Due to it’s size your export has been scheduled and will be processed after 6PM AEST";
      }
      this.showNotification(notificationMessage, 'Processing....');
    },
    showNotification(message, title) {
      this.$modal.show(AlertDialog, {
        title: title,
        message: message,
      });
    },
    async getApplicationList(appIds) {
      try {
        this.showModal('Please wait while the getting the application list.', 'Loading....');
        this.applicationIds = appIds;
        // reset pagination
        this.$store.commit('set_pagination', {
            backend_pagination: true,
            per_page: 50,
            page: 1
        });
        const pagination = this.$store.state.pagination;
        const response = await axios.post("/api/v1/reporting/getApplicationMetaData", {appIds: appIds, pagination: pagination});
        this.$store.commit('set_pagination', response.data.pagination);
        this.totalApplications = response.data.results;
        this.showApplications = true;
      } catch (e) {
        console.log(e);
      }
      this.closeModal();
    },
    showHideApps () {
      this.showApplications = !this.showApplications;
    },
    openApplicationSummary(application) {
      this.showHideApps();
      this.$router.push(`/application/${application.id}/summary`);
    },
    openApplicationEditor(application) {
      this.showHideApps();
      this.$router.push(`/application/${application.id}/capture`);
    },
    async openApplicationDocuments(application) {
        await this.$store.dispatch("load_application", application.id);
        this.$router.push(`/application/${application.id}/documents`);
    },
    openManageOwnersDialog(application) {
      this.$store.commit('set_application_update_owners_dialog', application);
    },
    openUpdateStatusDialog(application) {
      this.$store.commit('set_application_update_status_dialog', application);
    },
    async openCommentDialog(application) {
       await this.$store.dispatch("open_comment_dialog", application);
    },
    async onCloneApplication(application) {
        const cloneApplication = { ...application };
        const response = await this.$modal.show(OptionsDialog,{
          title: this.system_messages?.clone_application?.title,
          message: this.system_messages?.clone_application?.message,
          confirmText: this.system_messages?.clone_application?.confirmText,
          cancelText: this.system_messages?.clone_application?.cancelText,
          options: {
            includeDocs: { label: "Documents", value: true },
            includeComment: { label: "Comments", value: true }
          }
        });

        if (response) {
          this.$modal.show(WaitingDialog, {
              title: this.system_messages?.clone_application?.title,
              message: this.system_messages?.clone_application?.waitingText,
            });

          await this.$store.dispatch("clone_loan_application", {
            application_id: cloneApplication.id,
            includeDocs: response?.options?.includeDocs ?? false,
            includeComment: response?.options?.includeComment ?? false,
          });

          const application =  this.$store.getters.application;
          if (this.$modal.submit) {
              this.$modal.submit();
          }

          this.openApplicationEditor(application.loan);
        }
    },
    async refreshData() {
      try {
        this.paginationLoading = true;
        const pagination = this.$store.state.pagination;
        const response = await axios.post("/api/v1/reporting/getApplicationMetaData", {appIds: this.applicationIds, pagination: pagination});
        this.$store.commit('set_pagination', response.data.pagination);
        this.totalApplications = response.data.results;
      } catch (e) {
        console.log(e);
      } finally {
        this.paginationLoading = false;
      }
    }
  },
  async mounted() {
    try {
      await this.getPreGraphData();
    } catch (err) {
      console.log(err);
    }
    // Handle initial tab setup based on the route
    this.updateTabFromRoute(this.$route.params.tab);

    // At this point, `updateTabFromRoute` should handle the correct tab switching
    this.initialTabSetup = false;

    // BAUU-421 hide from all users
    // if (this.userHasPermission("access administration-page")
    //   || this.userHasRole("Broker")
    //   || this.userHasRole("In-Pod")
    //   || this.userHasRole("All-Pod")) {
    //   this.showServiceTab = true;
    // }

    if (this.userHasPermission("access administration-page")) {
      this.showSettlementTab = true;
    }

    if (this.userHasRole("super-admin")
      || this.userHasRole("Accounting Officer")
      || this.userHasRole("Ausloans Manager")
      || this.userHasRole("Broker")
      || this.userHasRole("In-Pod")
      || this.userHasRole("All-Pod")) {
      this.showBillings = true;
    }

    if (this.userHasRole("super-admin")) {
      this.showLeads = true;
    }

    if (this.userHasPermission('manage user_pods')) {
        await this.$store.dispatch('fetch_administration_user_pods', false);
    }

    // BAUU-421 hide from all users
    // if (this.userHasRole("super-admin")
    //   || this.userHasRole("In-Pod")
    //   || this.userHasRole("Broker")) {
    //   this.showBrokerAuctionTab = true;
    //   this.showBrokerSaleTab = true;
    // }
  },
  computed: {
    ...mapGetters([
      "options",
      "userHasPermission",
      "userHasRole",
      "user",
      "isPodManager",
      "leadRequests",
      "all_makers",
      "models_make",
      "system_messages"
    ]),
    hasChildPods() {
        return (this.filters.dropdown.pod !== "All" && this.getChildPods.length);
    },
    podIcon () {
      if (this.allChildPods) return 'mdi-close-box'
      if (this.somePods) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    getPods() {
      return this.$store.state.administration_pods ?? this.$store.state.user.user_pods ?? [];
    },
    getChildPods() {
        let childPods = [];
        if (this.filters.dropdown.pod !== 'All') {
            let pod = this.filters.dropdown.pod;
            let searchPods = this.getPods;
            let podFull = searchPods.find(item => item.name == pod);
            if (podFull) {
                for (let x in searchPods) {
                    if (searchPods[x].parent_id == podFull.id) {
                        childPods.push(searchPods[x]);
                    }
                }
            }
        }
        return childPods
    },
    allChildPods () {
      return this.filters.dropdown.child_pods.length === this.getChildPods.length;
    },
    userTagsStatuses() {
        if (!this.brokerList || !this.brokerList.statuses) {
            return {};
        }

        let statusMap = {};
        Object.keys(this.brokerList.statuses).forEach((name) => {
            statusMap[name] = this.isBrokerActive(name);
        });

        return statusMap;
    },
    structuredIntroducerList() {
      const parents = this.introducerList.filter(x => x.parent_id === null);
      const children = this.introducerList.filter(x => x.parent_id !== null);

      let parentsWithChildrenOnTop = [];
      let parentsWithChildren = [];
      let parentsWithoutChildren = [];

      parents.forEach(parent => {
        let hasChildren = false;
        let parentEntry = {
          ...parent,
          isParent: true,
          toLowerCase: () => parent.name.toLowerCase(),
          toString: () => parent.name,
        };
        children.forEach(child => {
          if (child.parent_id == parent.introducer_id) {
            if (!hasChildren) {
              parentsWithChildren.push(parentEntry);
              hasChildren = true;
            }
            parentsWithChildren.push({
              ...child,
              isParent: false,
              toLowerCase: () => child.name.toLowerCase(),
              toString: () => child.name,
            });
          }
        });
        if (!hasChildren) {
          parentsWithoutChildren.push(parentEntry);
        }
      });
      parentsWithChildrenOnTop = [...parentsWithChildren, ...parentsWithoutChildren];
      return parentsWithChildrenOnTop;
    },
  },
  watch:{
    "filters.dropdown.make"() {
      if (this.filters.dropdown.model != "All") {
        this.filters.dropdown.model = null;
      }
      this.fetch_model_by_make();
    },
    'filters.dropdown.child_pods'() {
        this.filters.dropdown.broker = [];
        this.updateBrokersAndReferrersDueToChildPodUpdate();
    },
    //watch changes in filter pod
    'filters.dropdown.pod'() {
        this.filters.dropdown.child_pods = [];
        this.filters.dropdown.broker = [];
        this.getPodBasedBrokers(this.filters.dropdown.pod);
    },
    'filters.dropdown.asset_type'() {
      if (typeof(this.filters.dropdown.asset_type) === 'string' &&
        this.filters.dropdown.asset_type.length > 0 &&
        (this.filters.dropdown.asset_type != 'All')) {
        this.filters.dropdown.asset_type = [this.filters.dropdown.asset_type];
      }

      if (this.filters.dropdown.asset_type[0] === "Vehicle"  && this.userHasRole("super-admin") ) {
        this.showAssetFilters = true;
        this.fetch_all_make();
      }
      else {
        this.showAssetFilters = false;

        this.filters.dropdown.model_year = null;
        this.filters.dropdown.make = null;
        this.filters.dropdown.model = null;
      }
    },
    'filters.dropdown.broker'() {
      if (typeof(this.filters.dropdown.broker) === 'string' &&
        this.filters.dropdown.broker.length > 0 &&
        this.filters.dropdown.broker != 'All') {
        this.filters.dropdown.broker = [this.filters.dropdown.broker];
      }
    },
    'filters.dropdown.lender'() {
      if (typeof(this.filters.dropdown.lender) === 'string' &&
        this.filters.dropdown.lender.length > 0) {
        this.filters.dropdown.lender = [this.filters.dropdown.lender];
      }
    },
    'filters.dropdown.introducer': {
      handler(newValue) {
        if (typeof newValue === 'string') {
          const matchingIntroducer = this.structuredIntroducerList.find(
            item => item.name.toLowerCase() === newValue.toLowerCase()
          );
          this.filters.dropdown.introducer = matchingIntroducer || null;
        }
      },
      immediate: true,
    },
    'filters.date_filter.end_date'() {
      if (!this.filters.date_filter.end_date) {
        this.filters.date_filter.end_date = moment().format('YYYY-MM-DD');
      }
    },
    'filters.date_filter.start_date'() {
      if (!this.filters.date_filter.start_date) {
        this.filters.date_filter.start_date = moment().startOf('month').format('YYYY-MM-DD');
      }
    },
    '$route.params.tab'(newTab) {
      this.updateTabFromRoute(newTab);
    },
  },
  async beforeCreate() {
    const params = {
      types: [
        'broker_pods',
        'lenders',
        'loan_asset_type',
        'model_years',
        'user_pods',
        'roles',
      ],
    }
    await this.$store.dispatch('fetch_types', { params });
  },
}
</script>

<style scoped lang="scss">
@import '../assets/scss/theme.scss';
@import '../assets/scss/md-custom/_ausloans-colors.scss';
.coming-soon {
  text-align: center;
}

.filter-box {
  margin-top: 10px;
}

.application-popup-overlay {
  opacity: 0.7;
  background: #000;
  width: 100%;
  height: 100%;
  z-index: 5;
  top: 0;
  left: 0;
  position: fixed;
}

.application-popup {
  position: absolute;
  background: white;
  border: 1px grey solid;
  overflow-y:scroll;
  overflow-x:hidden;
  height: 75%;
  z-index: 6;
  top: 10%;
  left: 10%;
  right: 10%;
  padding-bottom: 20px;
}
.section-tabs {
    margin-top: 25px;

    &::v-deep .md-tabs-navigation {
        position: sticky;
        top: 0px;
        z-index: 2;
        background: white !important;

        & .md-active {
            background-color: $ausloans-blue !important;
            color: white !important;
        }
    }

    &::v-deep .md-tabs-content {
        background: transparent !important;

        & .md-tab {
            padding-left: 0px;
            padding-right: 0px;
        }
    }
}

.text-center {
    justify-content: center;
}

.parent-referrer {
    font-weight: bold;
}

.child-referrer {
    margin-left: 10px;
}
</style>
